import isNumber from "lodash/isNumber";

import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from "../constants";

export const currencyFormatter = new Intl.NumberFormat(DEFAULT_LOCALE, {
  style: "currency",
  currency: DEFAULT_CURRENCY,
});

export const roundCents = (value: number): number =>
  parseFloat(value.toFixed(2));

export const formatAmount = (
  amount: number,
  locale = DEFAULT_LOCALE
): string => {
  return `${Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
  }).format(amount)}`;
};

export const formatAmountCurrency = (
  number: number | undefined,
  currency: string = DEFAULT_CURRENCY,
  options: Intl.NumberFormatOptions = {}
) => {
  if (typeof number === "undefined") return "";
  try {
    return new Intl.NumberFormat(DEFAULT_LOCALE, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      ...options,
    }).format(number);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`Invalid currency code: ${currency}`, e);
    // Drop currency-style options so the fallback formatter itself can never
    // throw (a stray `style: "currency"` with no valid currency re-throws).
    const safeOptions: Intl.NumberFormatOptions = { ...options };
    delete safeOptions.style;
    delete safeOptions.currency;
    delete safeOptions.currencyDisplay;
    delete safeOptions.currencySign;
    return `${new Intl.NumberFormat(DEFAULT_LOCALE, {
      minimumFractionDigits: 2,
      ...safeOptions,
    }).format(number)} (${currency})`;
  }
};

export const formatIntegerAmountCurrency = (
  amountInCents: number | undefined,
  currency: string = DEFAULT_CURRENCY
) => {
  if (amountInCents == null) {
    return "";
  }
  const amountInEuro = amountInCents / 100;
  return formatAmountCurrency(amountInEuro, currency, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export const formatAmountInCents = (
  amountInCents: number,
  showCurrency: boolean = false,
  locale?: string,
  currency: string = DEFAULT_CURRENCY
): string => {
  const amountInEuro = amountInCents / 100;
  return showCurrency
    ? formatAmountCurrency(amountInEuro, currency)
    : formatAmount(amountInEuro, locale);
};

/**
 * Format amount into DE format, for example: 5.000,00 €
 */
export const formatDEAmount = (
  amount?: number,
  currency: string = DEFAULT_CURRENCY
): string | undefined =>
  isNumber(amount)
    ? formatAmountInCents(amount!, true, undefined, currency)
    : undefined;

/**
 * Parse amount in string into number.
 * Note:
 * - Users can enter any amount like 5.000,00, 500000, or 5000,00.
 * - If the amount is invalid, it will return 0 instead of NaN.
 */
export const parseAmount = (amount: string): number => {
  // Remove all non-integer characters.
  const num = amount.replace(/[^0-9-]/g, "");
  const parsedNum = parseInt(num, 10);
  return isNaN(parsedNum) ? 0 : parsedNum;
};

/**
 * Format the input value in to currency.
 * Examples:
 * - "100,95 €" => "100,95 €"
 * - "100,95" => "100,95 €"
 * - "100,9" => "100,90 €"
 * - "100" => "100,00 €"
 * - "100.00" => "10.000,00 €"
 * - "abc" => "0,00 €"
 * - "" => ""
 * It also supports negative amount
 * - "-100,9" => "-100,90 €"
 */
export const formatAmountToCurrency = (
  amount: string,
  negativeEnabled?: boolean,
  currency: string = DEFAULT_CURRENCY
): string => {
  if (amount === "") {
    return "";
  }

  // If the first character is "-", consider it's negative amount
  const isNegative = negativeEnabled ? amount.charAt(0) === "-" : false;
  const amountParts = amount.split(",");

  let parsedAmount;

  if (amountParts.length < 2) {
    parsedAmount = parseAmount(`${amount}00`);
  } else {
    let [euros, cents] = amountParts.map((amount: string): string =>
      amount.replace(/[^0-9]/g, "")
    );

    // Make sure number of cents to be 2.
    cents = `${cents}0`.substr(0, 2);

    parsedAmount = parseAmount(`${euros}${cents}`);
  }

  // Only apply negative prefix if parsedAmount !== 0
  // Otherwise - 0,00 € doesn't make any sense.
  const prefix = isNegative && parsedAmount > 0 ? "-" : "";

  return isNumber(parsedAmount)
    ? prefix + formatDEAmount(parsedAmount, currency)!
    : "";
};

const isNumeric = (param: string | number) => {
  const paramType = typeof param;
  const restrictedTypes = [
    "undefined",
    "object",
    "boolean",
    "symbol",
    "function",
    "bigint",
  ];
  const allowedTypes = ["number"];
  if (restrictedTypes.includes(paramType)) return false;
  if (allowedTypes.includes(paramType)) return true;
  if (paramType === "string") {
    if (String(param).trim() === "") return false;
    return !isNaN(Number(param));
  }
  return false;
};

export const eurosToCents = (euros: number | string): number => {
  if (!euros || !isNumeric(euros)) return 0;
  return Number(`${euros}e2`);
};

/**
 * Converts the given amount from cents to Euros by dividing it by 100.
 * If number is undefined it returns 0 as default value.
 */
export const centsToEuros = (amount?: number): number =>
  amount ? roundCents(amount / 100) : 0;

/**
 * Takes a number in cents and rounds it down to the nearest euro.
 * @param  {number} value Amount in cents
 * @returns {number} Rounded down amount in cents
 */

export const roundDownCents = (value: number): number =>
  Math.floor(value / 100) * 100;

/**
 * Get the currency symbol for a given currency code
 * @param currencyCode - ISO 4217 currency code (e.g., "EUR", "USD", "GBP")
 * @param locale - Locale for formatting (defaults to DEFAULT_LOCALE)
 * @returns Currency symbol (e.g., "€", "$", "£")
 */
export const getCurrencySymbol = (
  currencyCode: string,
  locale: string = DEFAULT_LOCALE
): string => {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
    }).formatToParts(0);

    const currencyPart = parts.find((part) => part.type === "currency");
    return currencyPart?.value || currencyCode;
  } catch {
    return currencyCode;
  }
};
